import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Checkbox, Modal, Select, Space, Tag, Tooltip, Typography } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { TbRepeat } from "react-icons/tb";
import { Error, Success } from "../../../commons/Messages/Messages";
import axios from "axios";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import AddUpdateInvoice from "./AddInvoices";
import moment from "moment";
import DropdownComp from "../../Dropdowns/DropdownComp";
import GeneralTable from "../../GeneralTable";
import {
  NEGATIVE_TYPES,
  invoiceStatusMeta,
  invoiceTypeLabels
} from "../../../utils/invoiceStatusArray";
import * as accounting from "../../../serviceProvider/components/accounting/receivables/api";
import { canAccounting } from "./invoiceErrors";
import { checkSpecificPermission } from "../../../utils/checkPermissions";
import { getDomainFromToken, isAuthenticated } from "../../Auth/auth";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import PrintDialog from "./PrintDialog";
import { formatCents } from "../../../serviceProvider/components/accounting/shared/money";

import { branchLabel, branchOfDoc } from "../customerBranches";
import { useTranslation } from "react-i18next";
import "./Invoices.css";
import SiteDocsPanel from "../../../serviceProvider/components/siteDocs/SiteDocsPanel";
import { canUseSiteDocs } from "../../../serviceProvider/components/siteDocs/access";

/** Gross amount of an invoice: cents from the accounting fields, or the legacy euro strings.
 * Corrections and Stornos reduce the customer's debt and are shown negative. */
export const invoiceGross = inv => {
  if (inv?.amounts?.byRate?.length) {
    const gross = Number(inv.amounts.gross || 0);
    return formatCents(NEGATIVE_TYPES.includes(inv.type) ? -Math.abs(gross) : gross, {
      currency: inv.currency
    });
  }
  const net = parseFloat(inv?.totalAmount || 0);
  const rate = parseFloat(inv?.tax || 0);
  return formatCents(Math.round(net * (1 + rate / 100) * 100), { currency: inv?.currency });
};

const TYPE_FILTERS = [
  { value: "invoice", label: "Invoice" },
  { value: "partial", label: "Partial invoice" },
  { value: "advance", label: "Advance invoice" },
  { value: "final", label: "Final invoice" },
  { value: "credit_note", label: "Invoice correction" },
  { value: "cancellation", label: "Cancellation invoice" },
  { value: "correction_notice", label: "Correction notice" },
  {
    value: "credit_note,cancellation,correction_notice",
    label: "All corrections and cancellations"
  }
];

export const InvoiceStatusTag = ({ status }) => {
  const { t } = useTranslation("accounting");
  const meta = invoiceStatusMeta(status);
  return <Tag color={meta.tag}>{t(`status.${meta.status}`)}</Tag>;
};

const InvoicesList = ({
  financialController,
  data,
  updateParent,
  pageNo,
  pageSize,
  totalCount,
  setPageNo
}) => {
  const { t } = useTranslation("dashboard");
  const { t: ta } = useTranslation("accounting");
  const [loading, setLoading] = useState(false);
  const [docsInvoice, setDocsInvoice] = useState(null);
  const [printInvoice, setPrintInvoice] = useState(null);
  // UI-5: type / written off / has credit filters query the accounting list instead of the
  // rows handed in by the page. On a project page the rows share one project; keep that scope.
  const showFilters = canAccounting("Read");
  const [filters, setFilters] = useState({ type: undefined, writtenOff: false, hasCredit: false });
  const [filtered, setFiltered] = useState({ rows: [], count: 0, page: 1 });
  const filtering = Boolean(filters.type || filters.writtenOff || filters.hasCredit);
  const rowProjects = [
    ...new Set((data || []).map(r => String(r?.project?._id || r?.project || "")))
  ];
  const projectScope = rowProjects.length === 1 && rowProjects[0] ? rowProjects[0] : null;
  const filterPageSize = pageSize || 15;

  useEffect(() => {
    if (!filtering) return;
    let alive = true;
    setLoading(true);
    const params = {
      type: filters.type || undefined,
      writtenOff: filters.writtenOff ? "true" : undefined,
      hasCredit: filters.hasCredit ? "true" : undefined,
      page: projectScope ? 0 : filtered.page - 1,
      pageSize: projectScope ? 200 : filterPageSize
    };
    accounting
      .listInvoices(params)
      .then(res => {
        if (!alive) return;
        let rows = Array.isArray(res.data?.invoices) ? res.data.invoices : [];
        let count = Number(res.data?.count || 0);
        if (projectScope) {
          rows = rows.filter(r => String(r?.project?._id || r?.project || "") === projectScope);
          count = rows.length;
          rows = rows.slice((filtered.page - 1) * filterPageSize, filtered.page * filterPageSize);
        }
        setFiltered(prev => ({ ...prev, rows, count }));
      })
      .catch(err => alive && Error(accounting.errorText(err)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filtering, filters, filtered.page, projectScope]);

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setFiltered(prev => ({ ...prev, page: 1 }));
  };
  const user = isAuthenticated();
  let serviceProviderId = isAuthenticated()?.employer?._id
    ? isAuthenticated()?.employer?._id
    : isAuthenticated()?.serviceProviderId;

  const deleteHandler = async id => {
    await axios
      .delete(`${process.env.REACT_APP_BACKEND_URL}/invoices/delete/${id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      })
      .then(res => {
        if (res.status === 200 || res.status === 304) {
          Success(res.data.successMessage);
          updateParent();
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        if (err?.response?.data?.code === "LOCKED")
          Error(
            ta(
              "This invoice was sent and is locked. Cancel it with a cancellation invoice instead."
            )
          );
        else generalErrorAlert(err);
      });
  };

  const columns = [
    {
      title: ta("Invoice number"),
      dataIndex: "invoiceNumber",
      mobile: "title",
      sorter: (a, b) => (a.invoiceNumber || "").localeCompare(b.invoiceNumber || ""),
      render: (text, obj) => (
        <div>
          <div>
            <Link to={`/invoices/details/${obj?._id}`}>
              {text || <Typography.Text type="secondary">{ta("Draft")}</Typography.Text>}
            </Link>
          </div>
          {obj?.type && obj.type !== "invoice" ? (
            <Tag>{ta(invoiceTypeLabels[obj.type] || obj.type)}</Tag>
          ) : null}
          {obj?.recurringInvoice ? (
            <Link to="/user/invoices?view=recurring">
              <Tag color="blue" icon={<TbRepeat style={{ marginInlineEnd: 4 }} />}>
                {ta("Recurring")}
              </Tag>
            </Link>
          ) : null}
          {obj?.cancels ? (
            <div className="invoices-list__ref">
              {ta("Refers to invoice")}{" "}
              <Link to={`/invoices/details/${obj.cancels?._id || obj.cancels}`}>
                {obj.cancels?.invoiceNumber || ta("Open")}
              </Link>
            </div>
          ) : null}
        </div>
      )
    },
    {
      title: t("Title"),
      dataIndex: "title",
      render: text => <span style={{ overflowWrap: "anywhere" }}>{text}</span>,
      sorter: (a, b) => a.title?.localeCompare(b.title)
    },
    {
      title: t("Client"),
      dataIndex: "client",
      sorter: (a, b) => (a.client?.firstName || "").localeCompare(b.client?.firstName || ""),
      render: (text, obj) => (
        <div className="">
          {obj?.client?.company || ""} {obj?.client?.firstName} {obj?.client?.lastName}
          {branchOfDoc(obj) ? (
            <small style={{ display: "block", color: "var(--ds-text-muted)" }}>
              {branchLabel(branchOfDoc(obj))}
            </small>
          ) : null}
        </div>
      )
    },
    {
      title: t("Project"),
      dataIndex: "project",
      mobile: "hidden",
      render: (text, obj) => <div className="">{obj?.project?.projectName}</div>
    },
    {
      title: ta("Invoice date"),
      dataIndex: "issueDate",
      render: (text, obj) => (
        <div>
          {text ? moment(text).format("DD.MM.YYYY") : moment(obj.createdAt).format("DD.MM.YYYY")}
        </div>
      )
    },
    {
      title: t("Due Date"),
      dataIndex: "dueDate",
      mobile: "hidden",
      render: text => <div>{text ? moment(text).format("DD.MM.YYYY") : "–"}</div>
    },
    {
      title: ta("Gross"),
      key: "gross",
      align: "right",
      render: (_, obj) => <div style={{ whiteSpace: "nowrap" }}>{invoiceGross(obj)}</div>
    },
    {
      title: ta("Open"),
      key: "open",
      align: "right",
      render: (_, obj) =>
        obj?.lockedAt ? (
          <Space direction="vertical" size={0} align="end">
            <strong style={{ whiteSpace: "nowrap" }}>
              {formatCents(obj?.amounts?.open || 0, { currency: obj.currency })}
            </strong>
            {obj?.amounts?.writtenOff ? (
              <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                {ta("Written off")}{" "}
                {formatCents(obj.amounts.writtenOff, { currency: obj.currency })}
              </Typography.Text>
            ) : null}
          </Space>
        ) : (
          "–"
        )
    },
    {
      title: t("Download"),
      dataIndex: "d",
      mobile: "actions",
      render: (text, obj) => (
        <Tooltip
          title={obj?.lockedAt ? ta("PDF with embedded e-invoice (ZUGFeRD)") : ta("Draft preview")}
        >
          <Button type="primary" onClick={() => setPrintInvoice(obj)}>
            {t("Print Invoice")}
          </Button>
        </Tooltip>
      )
    },
    {
      title: t("Status"),
      dataIndex: "status",
      mobile: "subtitle",
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      render: text => <InvoiceStatusTag status={text} />
    },
    {
      title: t("Action"),
      key: "action",
      mobile: "actions",
      render: (text, obj) => {
        const domain = getDomainFromToken();
        const token = localStorage.getItem("token");
        let menuItems = [
          {
            key: "view",
            link: `${process.env.REACT_APP_BACKEND_URL}/invoices/preview/${obj?._id}?domain=${domain}&token=${token}`
          }
        ];
        // Photos and notes from the site assigned to this invoice
        if (canUseSiteDocs()) {
          menuItems.push({
            key: "siteDocs",
            component: () => (
              <Button
                type="link"
                style={{ color: "var(--ds-text)" }}
                onClick={() => setDocsInvoice(obj)}
              >
                <CameraOutlined /> {t("Documentation")}
              </Button>
            )
          });
        }
        const locked = Boolean(obj?.lockedAt);
        if (
          !locked &&
          (financialController ||
            checkSpecificPermission(user?.roleId?.permissions, "Invoices", "Update"))
        ) {
          menuItems = [
            ...menuItems,
            {
              key: "edit",
              component: () => (
                <AddUpdateInvoice
                  serviceProviderId={serviceProviderId}
                  data={obj}
                  isUpdate={true}
                  updateParent={updateParent}
                />
              )
            }
          ];
        }
        if (!locked && checkSpecificPermission(user?.roleId?.permissions, "Invoices", "Delete")) {
          menuItems = [
            ...menuItems,
            {
              key: "delete",
              component: () => (
                <DeleteModal text="Invoice" onClick={() => deleteHandler(obj?._id)} />
              )
            }
          ];
        }
        return (
          <div>
            <DropdownComp menuItems={menuItems} />
          </div>
        );
      }
    }
  ];

  return (
    <div className="invoices-list">
      <PrintDialog
        open={Boolean(printInvoice)}
        invoiceId={printInvoice?._id}
        fileName={printInvoice?.invoiceNumber || `Rechnung-${printInvoice?._id}`}
        onClose={() => setPrintInvoice(null)}
      />
      <Modal
        open={Boolean(docsInvoice)}
        onCancel={() => setDocsInvoice(null)}
        footer={null}
        width={760}
        destroyOnClose
        title={`${t("Documentation")} · ${docsInvoice?.invoiceNumber || ""} ${docsInvoice?.title || ""}`}
      >
        {docsInvoice ? (
          <SiteDocsPanel
            filter={{ invoice: docsInvoice._id }}
            client={docsInvoice.client?._id || docsInvoice.client || ""}
            customerEmail={docsInvoice.client?.email || ""}
            reportTitle={docsInvoice.title || ""}
          />
        ) : null}
      </Modal>
      {showFilters ? (
        <div className="invoices-list__filters">
          <Select
            allowClear
            className="invoices-list__type"
            placeholder={ta("All types")}
            aria-label={ta("Type")}
            value={filters.type}
            onChange={v => setFilter("type", v)}
            options={TYPE_FILTERS.map(o => ({ value: o.value, label: ta(o.label) }))}
          />
          <Checkbox
            checked={filters.writtenOff}
            onChange={e => setFilter("writtenOff", e.target.checked)}
          >
            {ta("Written off")}
          </Checkbox>
          <Checkbox
            checked={filters.hasCredit}
            onChange={e => setFilter("hasCredit", e.target.checked)}
          >
            {ta("Customer has credit")}
          </Checkbox>
        </div>
      ) : null}
      {filtering ? (
        <GeneralTable
          loading={loading}
          pageNo={filtered.page}
          pageSize={filterPageSize}
          totalCount={filtered.count}
          data={filtered.rows}
          columns={columns}
          setPageNo={page => setFiltered(prev => ({ ...prev, page }))}
        />
      ) : (
        <GeneralTable
          loading={loading}
          pageNo={pageNo}
          pageSize={pageSize}
          totalCount={totalCount}
          data={data}
          columns={columns}
          setPageNo={setPageNo}
        />
      )}
    </div>
  );
};

export default InvoicesList;
